import { useState } from "react";
import styles from "./Wrapped.module.css";

// Real line/area chart for the Wrapped slides (rating journey + ELO journey).
// Follows the dataviz rules: one axis, recessive grid, y-ticks, sparse x date
// labels, hover tooltip on every point, selective direct label (highlight only),
// and W/L encoded by SHAPE (filled vs hollow) + tooltip text — never color alone.

export type ChartPoint = {
  value: number;
  dateLabel?: string;
  title?: string;   // tooltip line 1, e.g. "vs Fraudsters"
  sub?: string;     // tooltip line 2, e.g. "Ancient · Sejr"
  won?: boolean;    // filled (win) vs hollow (loss) marker; undefined = neutral
};

type Props = {
  points: ChartPoint[];
  color: string;            // series color (single series — no legend needed)
  baseline?: number;        // reference line, e.g. rating 1.0
  highlightIndex?: number;  // direct-labeled point (the best game)
  decimals?: number;        // value formatting
};

const W = 760;
const H = 250;
const PAD_L = 46;
const PAD_R = 18;
const PAD_T = 20;
const PAD_B = 30;

/** 3-5 "nice" y ticks covering [min, max]. */
const niceTicks = (min: number, max: number): number[] => {
  const span = Math.max(1e-6, max - min);
  const rawStep = span / 3;
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => span / s <= 4) ?? mag * 10;
  const start = Math.ceil(min / step) * step;
  const ticks: number[] = [];
  for (let v = start; v <= max + 1e-9; v += step) ticks.push(Number(v.toFixed(6)));
  return ticks;
};

const WrappedChart = ({ points, color, baseline, highlightIndex, decimals = 2 }: Props) => {
  const [hover, setHover] = useState<number | null>(null);

  if (points.length < 2) return null;

  const values = points.map((p) => p.value);
  let min = Math.min(...values, baseline ?? Infinity);
  let max = Math.max(...values, baseline ?? -Infinity);
  const pad = Math.max((max - min) * 0.12, decimals > 0 ? 0.05 : 12);
  min -= pad;
  max += pad;

  const x = (i: number) => PAD_L + (i / (points.length - 1)) * (W - PAD_L - PAD_R);
  const y = (v: number) => PAD_T + (1 - (v - min) / (max - min)) * (H - PAD_T - PAD_B);

  const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(" ");
  const areaPath = `${linePath} L${x(points.length - 1).toFixed(1)},${(H - PAD_B).toFixed(1)} L${x(0).toFixed(1)},${(H - PAD_B).toFixed(1)} Z`;

  const ticks = niceTicks(min, max);
  const fmt = (v: number) => v.toFixed(decimals);
  const showMarkers = points.length <= 26;
  const gradId = `wrGrad-${color.replace("#", "")}`;

  // Sparse x labels: first, middle, last (only where a dateLabel exists).
  const xTickIdx = Array.from(new Set([0, Math.floor((points.length - 1) / 2), points.length - 1]));

  const hovered = hover != null ? points[hover] : null;

  return (
    <div className={styles.chartBox} style={{ position: "relative" }}>
      <svg className={styles.chart} viewBox={`0 0 ${W} ${H}`} onMouseLeave={() => setHover(null)}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.35" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* recessive grid + y tick labels (text tokens, not series color) */}
        {ticks.map((tv) => (
          <g key={tv}>
            <line x1={PAD_L} x2={W - PAD_R} y1={y(tv)} y2={y(tv)} stroke="rgba(255,255,255,0.08)" />
            <text x={PAD_L - 8} y={y(tv) + 4} textAnchor="end" fontSize="11" fill="rgba(255,255,255,0.55)">{fmt(tv)}</text>
          </g>
        ))}

        {/* the meaningful reference line (e.g. rating 1.00) */}
        {baseline !== undefined && (
          <line x1={PAD_L} x2={W - PAD_R} y1={y(baseline)} y2={y(baseline)} stroke="rgba(255,255,255,0.30)" strokeDasharray="5 5" />
        )}

        {/* x date labels */}
        {xTickIdx.map((i) => points[i].dateLabel && (
          <text key={i} x={x(i)} y={H - 8} textAnchor={i === 0 ? "start" : i === points.length - 1 ? "end" : "middle"} fontSize="11" fill="rgba(255,255,255,0.55)">
            {points[i].dateLabel}
          </text>
        ))}

        <path d={areaPath} fill={`url(#${gradId})`} />
        <path d={linePath} fill="none" stroke={color} strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />

        {/* markers: win = filled green, loss = HOLLOW red (shape carries W/L, not color alone) */}
        {showMarkers && points.map((p, i) => {
          const won = p.won;
          const fill = won === undefined ? color : won ? "#4ade80" : "#181818";
          const stroke = won === false ? "#f87171" : "#181818";
          return (
            <circle
              key={i}
              cx={x(i)}
              cy={y(p.value)}
              r={hover === i ? 7 : 5.5}
              fill={fill}
              stroke={stroke}
              strokeWidth={won === false ? 2.5 : 2}
            />
          );
        })}

        {/* invisible fat hit targets for the hover layer */}
        {points.map((p, i) => (
          <circle key={`h${i}`} cx={x(i)} cy={y(p.value)} r={16} fill="transparent" onMouseEnter={() => setHover(i)} />
        ))}

        {/* direct label on the highlight only (selective labeling) */}
        {highlightIndex !== undefined && points[highlightIndex] && hover === null && (
          <g>
            <circle cx={x(highlightIndex)} cy={y(points[highlightIndex].value)} r={8} fill="none" stroke={color} strokeWidth={2} />
            <text
              x={x(highlightIndex)}
              y={y(points[highlightIndex].value) - 14}
              textAnchor="middle"
              fontSize="13"
              fontWeight="800"
              fill="rgba(255,255,255,0.92)"
            >
              {fmt(points[highlightIndex].value)}
            </text>
          </g>
        )}
      </svg>

      {hovered && hover != null && (
        <div
          className={styles.chartTooltip}
          style={{ left: `${(x(hover) / W) * 100}%`, top: `${(y(hovered.value) / H) * 100}%` }}
        >
          {hovered.title && <div className={styles.chartTooltipTitle}>{hovered.title}</div>}
          <div className={styles.chartTooltipValue}>{fmt(hovered.value)}</div>
          {hovered.sub && <div className={styles.chartTooltipSub}>{hovered.sub}</div>}
        </div>
      )}
    </div>
  );
};

export default WrappedChart;
